import React from 'react';
import PropTypes from 'prop-types';
import TransitionLink from 'components/transition-link/transition-link';
import classNames from 'classnames';
import MakeHash from 'make-hash';
import {TweenMax, Power1} from 'gsap';
import Button from 'components/button/button';
import Icon from 'components/icons/icon';
import Text from 'components/text/text';

export default class Dropdown extends React.Component {
  constructor (props) {
    super(props);

    this.state = {
      'isOpen': false,
      'triggerTxt': props.triggerTxt
    };

    this.setUpHandlers();
  }

  /**
   * Binds any handlers to the correct context
   *
   * @method  setUpHandlers
   */
  setUpHandlers() {
    this.onToggleClick = this.handleToggleClick.bind(this);
    this.onDocumentClick = this.handleDocumentClick.bind(this);
  }

  /**
   * React Lifecycle Hook
   * Invoked before a mounted component receives new props. If you need to
   * update the state in response to prop changes, you may compare this.props
   * and nextProps and perform state transitions
   *
   * @method  componentWillReceiveProps
   */
  componentWillReceiveProps(nextProps) {
    if (nextProps.triggerTxt !== this.props.triggerTxt) {
      this.setState({
        'triggerTxt': nextProps.triggerTxt
      });
    }
  }

  /**
   * Toggles visibility of dropdown based on
   * previous isOpen state
   *
   * @method  toggleVisibility
   */
  toggleVisibility() {
    this.setState({
      'isOpen': !this.state.isOpen
    }, () => {
      this.tweenOptions();
      this.toggleOffClickListener()
    });
  }

  tweenOptions() {
    const $optionList = this.dropdownEl.querySelector('.js-option-list');
    const $options = Array.from(this.dropdownEl.querySelectorAll('.js-option'));

    $options.forEach($option => {
      const $text = $option.querySelector('.js-option-text');

      TweenMax.to($option, 0.5, {
        'height': this.state.isOpen ? $option.scrollHeight : 0,
        'ease': this.state.isOpen ? Power1.easeOut : Power1.easeIn,
        'onStart': () => {
          if (this.state.isOpen) {
            $optionList.classList.add('dropdown-options_show');
          }
        },
        'onComplete': () => {
          if (!this.state.isOpen) {
            $optionList.classList.remove('dropdown-options_show');
          }
        }
      });

      TweenMax.to($text, 0.5, {
        'y': this.state.isOpen ? 0 : -15,
        'opacity': this.state.isOpen ? 1 : 0,
        'ease': this.state.isOpen ? Power1.easeOut : Power1.easeIn
      });
    });
  }

  /**
   * Handles user click of the dropdown button
   *
   * @method handleToggleClick
   */
  handleToggleClick(event, option) {
    this.toggleVisibility();

    if (option && this.props.dynamicTrigger) {
      this.setTriggerText(option);
    }
  }

  /**
   * Handles user click on document when a dropdown is open
   *
   * @method handleDocumentClick
   */
  handleDocumentClick(event) {
    const isInDropdown = event.target.closest('.js-dropdown');

    if (isInDropdown === null || isInDropdown === undefined) {
      this.toggleVisibility();
    }
  }

  setTriggerText(option) {
    this.setState({ 'triggerTxt': option.description });
  }

  /**
   * Sets or removes a document listener while the dropdown is open
   *
   * @method  toggleOffClickListener
   */
  toggleOffClickListener() {
    if (this.state.isOpen) {
      document.addEventListener('click', this.onDocumentClick);
    } else {
      document.removeEventListener('click', this.onDocumentClick);
    }
  }

  /**
   * Builds a list of React Router DOM <Link> objects for a dropdown
   *
   * @method  _renderLinkOptions
   * @params  {Array} linkOptions [The collection of link objects that will be build into <Links>]
   * @params  {String} optionsClasses [The classes that will be applied to the <Link> list]
   */
  _renderLinkOptions(linkOptions, optionsClasses) {
    if (!linkOptions) return;
    return(
      linkOptions.map((option, i) => {
        return(
          <li className="dropdown-option js-option" key={MakeHash(`dropdown-option-${i}`)}>
            <TransitionLink
              to={option.path}
              onClick={(event) => { this.handleToggleClick(event, option); }}
            >
              <span className="dropdown-option-text js-option-text">
                {option.description}
              </span>
            </TransitionLink>
          </li>
        );
      })
    );
  }

  /**
   * Builds a list of <Button> objects for a dropdown
   *
   * @method  _renderLinkOptions
   * @params  {Array} buttonOptions [The collection of button objects that will be build into <Links>]
   * @params  {String} optionsClasses [The classes that will be applied to the <Button> list]
   */
  _renderButtonOptions(buttonOptions) {
    if (!buttonOptions) return;
    return(
      buttonOptions.map((option, i) => {
        return(
          <li className="dropdown-option js-option" key={MakeHash(`dropdown-option-${i}`)}>
            <button
              onClick={(event) => {
                option.action(option);
                this.handleToggleClick(event, option);
              }}
              type="button"
            >
              <span className="dropdown-option-text js-option-text">
                {option.description}
              </span>
            </button>
          </li>
        );
      })
    );
  }

  render() {
    const { className, theme, linkOptions, buttonOptions, children, triggerTxt, iconType, animate, label } = this.props;
    const dropdownClasses = classNames(
      'dropdown js-dropdown',
      {
        [`dropdown_${theme}`]: theme,
        'dropdown_open': this.state.isOpen,
        'dropdown_hasLabel': label,
        [`mix-dropdown_${animate}`]: animate
      },
      className
    );
    const optionsClasses = classNames(
      'dropdown-options js-option-list'
    );
    const triggerClasses = classNames(
      'dropdown-trigger',
      {
        'dropdown-trigger_plain': this.props.plainTrigger
      }
    );
    const labelMarkup = (label) ? <div className="dropdown-label">{label}</div> : '';

    return (
      <div
        className={dropdownClasses}
        ref={el => { this.dropdownEl = el; }}
      >
        {labelMarkup}
        <button className={triggerClasses} onClick={this.onToggleClick}>
          <span className="dropdown-triggerText">
            {this.state.triggerTxt}
          </span>
          <span className="dropdown-triggerIcon">
            <Icon
              fillColor="DefaultBlue"
              iconSize="sm"
              iconType="chevron"
            />
          </span>
        </button>
        <ul className={optionsClasses}>
          {this._renderLinkOptions(linkOptions, optionsClasses)}
          {this._renderButtonOptions(buttonOptions, optionsClasses)}
        </ul>
        {children}
      </div>
    );
  }
}

const linkOptionShape = PropTypes.shape({
  'description': PropTypes.string.isRequired,
  'path': PropTypes.string.isRequired
});

const buttonOptionShape = PropTypes.shape({
  'action': PropTypes.func.isRequired,
  'description': PropTypes.string.isRequired
});

Dropdown.propTypes = {
  'dynamicTrigger': PropTypes.bool,
  'plainTrigger': PropTypes.bool,
  'triggerTxt': PropTypes.string,
  'theme': PropTypes.oneOf(['body', 'dark', 'light']),
  'linkOptions': PropTypes.arrayOf(linkOptionShape),
  'buttonOptions': PropTypes.arrayOf(buttonOptionShape),
  'iconType': PropTypes.string,
  'animate': PropTypes.oneOf(['bottomLeft', 'bottomRight']),
  'label': PropTypes.string
};



// WEBPACK FOOTER //
// ./src/client/components/dropdown/dropdown.jsx